import { useThreads } from '@copilotkit/react-core'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

type ChatMode = 'popup' | 'sidebar' | 'chat'

const VALID_MODES = ['popup', 'sidebar', 'chat'] as const

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768

export function useAiChatSession(resetConnection: () => void) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<ChatMode>(() => (isMobile() ? 'chat' : 'popup'))
  const pathname = usePathname()
  const { setThreadId } = useThreads()

  // Force chat mode on mobile
  useEffect(() => {
    const handleResize = () => {
      if (isMobile()) setMode('chat')
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Restore open/mode from session storage on navigation
  useEffect(() => {
    if (typeof window === 'undefined') return
    const savedOpen = sessionStorage.getItem('ai-chat-open')
    const savedMode = sessionStorage.getItem('ai-chat-mode') as ChatMode | null
    if (savedOpen === 'true' && !window.location.pathname.includes('/p/')) setOpen(true)
    if (!isMobile() && savedMode && (VALID_MODES as readonly string[]).includes(savedMode)) setMode(savedMode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Persist open/mode to session storage
  useEffect(() => {
    if (typeof window === 'undefined') return
    sessionStorage.setItem('ai-chat-open', String(open))
    sessionStorage.setItem('ai-chat-mode', mode)
  }, [open, mode])

  const switchMode = useCallback((newMode: ChatMode) => {
    setMode(newMode)
    setOpen(true)
  }, [])

  const handleClose = useCallback(() => setOpen(false), [])

  const handleReset = useCallback(() => {
    sessionStorage.removeItem('ai-chat-open')
    sessionStorage.removeItem('ai-chat-mode')
    resetConnection()
    setThreadId(crypto.randomUUID())
    setOpen(false)
    setTimeout(() => setOpen(true), 50)
  }, [resetConnection, setThreadId])

  return { open, setOpen, mode, switchMode, handleClose, handleReset }
}
